import { ref } from 'vue';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  limit,
  getDocs,
  deleteDoc,
  writeBatch,
  setDoc
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/firebase/init';

export const CONVERSATION_LIMIT = 50;
export const MESSAGE_LIMIT = 100;
export const MESSAGE_DELETE_LIMIT = 20;
export const MAX_GROUP_PARTICIPANTS = 10;
export const GROUP_IMAGE_PATH = 'groupChatPictures';
export const DEPARTMENT_IMAGE_PATH = 'departmentProfilePictures';
export const DEPARTMENT_PROFILE_LIMIT = 50;
export const ADMIN_PROFILE_LIMIT = 50;

const uniqueIds = (ids = []) => [...new Set(ids.filter(Boolean))];

export const getConversationType = (conversation = {}) => {
  conversation = conversation || {};

  if (['direct', 'group', 'department'].includes(conversation.type)) {
    return conversation.type;
  }

  return (conversation.participants || []).length > 2 ? 'group' : 'direct';
};

export const getDirectConversationKey = (participants = []) => {
  return uniqueIds(participants).sort().join('__');
};

const normalizeLookupText = (value = '') => {
  return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
};

export const compactParticipant = (user = {}) => {
  const university = user.university;
  let universityName = '';
  const rawProfilePicture = user.profilePicture || user.photoURL || '';
  const profilePicture = typeof rawProfilePicture === 'string'
    ? rawProfilePicture.trim()
    : '';

  if (typeof university === 'string') {
    universityName = university;
  } else if (university && typeof university === 'object') {
    universityName = university.name || '';
  }

  return {
    id: user.id || user.uid || '',
    name: user.name || user.displayName || user.email || 'Student',
    profilePicture: ['null', 'undefined'].includes(profilePicture.toLowerCase())
      ? ''
      : profilePicture,
    university: universityName ? { name: universityName } : {}
  };
};

export const compactDepartmentProfile = (profile = {}) => {
  return {
    id: profile.id || '',
    name: profile.name || 'Department',
    description: profile.description || '',
    verified: profile.verified !== false,
    profilePicture: profile.profilePicture || '',
    profilePicturePath: profile.profilePicturePath || '',
    isDepartment: true
  };
};

const normalizeParticipantDetails = (participants, participantDetails = []) => {
  const detailsById = participantDetails.reduce((lookup, participant) => {
    const compact = compactParticipant(participant);
    if (compact.id) lookup[compact.id] = compact;
    return lookup;
  }, {});

  return participants.map((participantId) => {
    return detailsById[participantId] || {
      id: participantId,
      name: 'Student',
      profilePicture: '',
      university: {}
    };
  });
};

export const getConversationTitle = (conversation, currentUserId) => {
  conversation = conversation || {};

  if (getConversationType(conversation) === 'department') {
    return conversation.departmentProfileSnapshot?.name || conversation.title || 'Department';
  }

  if (getConversationType(conversation) === 'group') {
    return conversation.title || 'Group chat';
  }

  const otherUser = (conversation.participantDetails || []).find(
    participant => participant.id !== currentUserId
  );
  return otherUser?.name || 'Student';
};

export const getConversationAvatar = (conversation, currentUserId) => {
  conversation = conversation || {};

  if (getConversationType(conversation) === 'department') {
    return conversation.departmentProfileSnapshot?.profilePicture || '';
  }

  if (getConversationType(conversation) === 'group') {
    return conversation.groupProfilePicture || '';
  }

  return (conversation.participantDetails || []).find(
    participant => participant.id !== currentUserId
  )?.profilePicture || '';
};

const isGroupAdmin = (conversation, userId) => {
  return getConversationType(conversation) === 'group' &&
    conversation?.createdBy === userId;
};

const canDeleteConversation = (conversation, userId) => {
  if (!conversation || !userId || !(conversation.participants || []).includes(userId)) {
    return false;
  }

  return getConversationType(conversation) === 'direct' || isGroupAdmin(conversation, userId);
};

const compressImage = async (file, maxWidth = 800, maxHeight = 800, quality = 0.82) => {
  const { default: Compressor } = await import('compressorjs');

  return new Promise((resolve, reject) => {
    new Compressor(file, {
      maxWidth,
      maxHeight,
      quality,
      convertSize: 0,
      mimeType: 'image/jpeg',
      success: resolve,
      error: reject
    });
  });
};

const syncDepartmentProfileToConversations = async (departmentProfileId, updates = {}, userId) => {
  const conversationsQuery = query(
    collection(db, 'conversations'),
    where('departmentProfileId', '==', departmentProfileId),
    limit(CONVERSATION_LIMIT)
  );
  const conversationsSnapshot = await getDocs(conversationsQuery);

  if (conversationsSnapshot.empty) return;

  const batch = writeBatch(db);
  conversationsSnapshot.docs.forEach((conversationDoc) => {
    const conversation = conversationDoc.data();
    const departmentSnapshot = {
      ...(conversation.departmentProfileSnapshot || {}),
      ...updates
    };
    const participantDetails = (conversation.participantDetails || []).map((participant) => {
      if (participant.id !== departmentProfileId) return participant;
      return {
        ...participant,
        ...(Object.prototype.hasOwnProperty.call(updates, 'name') ? { name: updates.name } : {}),
        ...(Object.prototype.hasOwnProperty.call(updates, 'profilePicture') ? { profilePicture: updates.profilePicture || '' } : {})
      };
    });

    batch.update(conversationDoc.ref, {
      departmentProfileSnapshot: departmentSnapshot,
      participantDetails,
      updatedBy: userId
    });
  });

  await batch.commit();
};

export const getConversationBetweenUsers = async (userA, userB) => {
  try {
    if (!userA || !userB) {
      return { success: false, message: 'Both users are required' };
    }

    const directKey = getDirectConversationKey([userA, userB]);
    const directKeyQuery = query(
      collection(db, 'conversations'),
      where('directKey', '==', directKey),
      limit(1)
    );
    const directKeySnapshot = await getDocs(directKeyQuery);

    if (!directKeySnapshot.empty) {
      const conversationDoc = directKeySnapshot.docs[0];
      return {
        success: true,
        conversation: {
          id: conversationDoc.id,
          ...conversationDoc.data()
        }
      };
    }

    const legacyQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userA),
      limit(CONVERSATION_LIMIT)
    );
    const legacySnapshot = await getDocs(legacyQuery);
    const conversationDoc = legacySnapshot.docs.find((item) => {
      const data = item.data();
      const participants = data.participants || [];
      return getConversationType(data) === 'direct' &&
        participants.length === 2 &&
        participants.includes(userA) &&
        participants.includes(userB);
    });

    if (!conversationDoc) {
      return { success: true, conversation: null };
    }

    return {
      success: true,
      conversation: {
        id: conversationDoc.id,
        ...conversationDoc.data()
      }
    };
  } catch (error) {
    console.error('Error finding direct conversation:', error);
    return { success: false, message: error.message };
  }
};

export const createDirectConversation = async (participants, participantDetails = []) => {
  try {
    const cleanParticipants = uniqueIds(participants);

    if (cleanParticipants.length !== 2) {
      return { success: false, message: 'Direct chats require two participants' };
    }

    const existing = await getConversationBetweenUsers(cleanParticipants[0], cleanParticipants[1]);
    if (existing.success && existing.conversation) {
      return {
        success: true,
        conversationId: existing.conversation.id,
        conversation: existing.conversation,
        message: 'Conversation already exists'
      };
    }

    const normalizedDetails = normalizeParticipantDetails(cleanParticipants, participantDetails);
    const directKey = getDirectConversationKey(cleanParticipants);
    const conversationRef = await addDoc(collection(db, 'conversations'), {
      type: 'direct',
      participants: cleanParticipants,
      participantDetails: normalizedDetails,
      directKey,
      createdBy: cleanParticipants[0],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessage: null
    });

    return {
      success: true,
      conversationId: conversationRef.id,
      conversation: {
        id: conversationRef.id,
        type: 'direct',
        participants: cleanParticipants,
        participantDetails: normalizedDetails,
        directKey,
        lastMessage: null
      }
    };
  } catch (error) {
    console.error('Error creating direct conversation:', error);
    return { success: false, message: error.message };
  }
};

export const createGroupConversation = async ({
  title,
  participants,
  participantDetails = [],
  createdBy
}) => {
  try {
    const cleanTitle = (title || '').trim();
    const cleanParticipants = uniqueIds(participants);

    if (!cleanTitle) {
      return { success: false, message: 'Group name is required' };
    }

    if (!createdBy || !cleanParticipants.includes(createdBy)) {
      return { success: false, message: 'Group creator must be a participant' };
    }

    if (cleanParticipants.length < 3 || cleanParticipants.length > MAX_GROUP_PARTICIPANTS) {
      return {
        success: false,
        message: `Groups need 3-${MAX_GROUP_PARTICIPANTS} total participants`
      };
    }

    const normalizedDetails = normalizeParticipantDetails(cleanParticipants, participantDetails);
    const conversationRef = await addDoc(collection(db, 'conversations'), {
      type: 'group',
      title: cleanTitle,
      participants: cleanParticipants,
      participantDetails: normalizedDetails,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessage: null
    });

    return {
      success: true,
      conversationId: conversationRef.id,
      conversation: {
        id: conversationRef.id,
        type: 'group',
        title: cleanTitle,
        participants: cleanParticipants,
        participantDetails: normalizedDetails,
        createdBy,
        lastMessage: null
      }
    };
  } catch (error) {
    console.error('Error creating group conversation:', error);
    return { success: false, message: error.message };
  }
};

export const getDepartmentProfiles = async (limitCount = DEPARTMENT_PROFILE_LIMIT) => {
  try {
    const departmentQuery = query(
      collection(db, 'departmentProfiles'),
      orderBy('name', 'asc'),
      limit(limitCount)
    );
    const departmentSnapshot = await getDocs(departmentQuery);
    const departmentProfiles = departmentSnapshot.docs.map(profileDoc => ({
      id: profileDoc.id,
      ...profileDoc.data()
    }));

    return { success: true, departmentProfiles };
  } catch (error) {
    console.error('Error loading department profiles:', error);
    return { success: false, message: error.message, departmentProfiles: [] };
  }
};

export const getAdminProfiles = async (limitCount = ADMIN_PROFILE_LIMIT) => {
  try {
    const adminQuery = query(
      collection(db, 'users'),
      where('role', '==', 'admin'),
      limit(limitCount)
    );
    const adminSnapshot = await getDocs(adminQuery);
    const admins = adminSnapshot.docs
      .map(adminDoc => compactParticipant({ id: adminDoc.id, ...adminDoc.data() }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { success: true, admins };
  } catch (error) {
    console.error('Error loading admin profiles:', error);
    return { success: false, message: error.message, admins: [] };
  }
};

export const createDepartmentProfile = async ({
  name,
  assignedAdminIds = [],
  createdBy
}) => {
  try {
    const cleanName = (name || '').trim();
    const cleanAdminIds = uniqueIds([createdBy, ...assignedAdminIds]);

    if (!cleanName) {
      return { success: false, message: 'Department name is required' };
    }

    if (!createdBy || !cleanAdminIds.includes(createdBy)) {
      return { success: false, message: 'Creator must be assigned to the department profile' };
    }

    if (cleanAdminIds.length > MAX_GROUP_PARTICIPANTS - 1) {
      return {
        success: false,
        message: `Assign up to ${MAX_GROUP_PARTICIPANTS - 1} admins per department profile`
      };
    }

    const normalizedName = normalizeLookupText(cleanName);
    const duplicateQuery = query(
      collection(db, 'departmentProfiles'),
      where('normalizedName', '==', normalizedName),
      limit(1)
    );
    const duplicateSnapshot = await getDocs(duplicateQuery);
    if (!duplicateSnapshot.empty) {
      const existingProfile = duplicateSnapshot.docs[0];
      return {
        success: true,
        departmentProfileId: existingProfile.id,
        departmentProfile: {
          id: existingProfile.id,
          ...existingProfile.data()
        },
        message: 'Department profile already exists'
      };
    }

    const departmentRef = await addDoc(collection(db, 'departmentProfiles'), {
      name: cleanName.slice(0, 60),
      normalizedName,
      verified: true,
      assignedAdminIds: cleanAdminIds,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      departmentProfileId: departmentRef.id,
      departmentProfile: {
        id: departmentRef.id,
        name: cleanName.slice(0, 60),
        normalizedName,
        verified: true,
        assignedAdminIds: cleanAdminIds,
        createdBy
      }
    };
  } catch (error) {
    console.error('Error creating department profile:', error);
    return { success: false, message: error.message };
  }
};

export const getDepartmentConversationId = (departmentProfileId, studentId) => {
  return `department_${departmentProfileId}_${studentId}`;
};

export const createOrGetDepartmentConversation = async ({
  departmentProfile,
  studentId,
  studentSnapshot
}) => {
  try {
    if (!departmentProfile?.id || !studentId) {
      return { success: false, message: 'Department and student are required' };
    }

    const conversationId = getDepartmentConversationId(departmentProfile.id, studentId);
    const conversationRef = doc(db, 'conversations', conversationId);
    const assignedAdminIds = uniqueIds(
      departmentProfile.assignedAdminIds?.length
        ? departmentProfile.assignedAdminIds
        : [departmentProfile.createdBy]
    );
    const participants = uniqueIds([studentId, ...assignedAdminIds]);
    const departmentSnapshot = compactDepartmentProfile(departmentProfile);
    const studentDetails = compactParticipant(studentSnapshot || { id: studentId });
    const participantDetails = [
      studentDetails,
      {
        ...departmentSnapshot,
        id: departmentProfile.id
      }
    ];

    await setDoc(conversationRef, {
      type: 'department',
      departmentProfileId: departmentProfile.id,
      departmentProfileSnapshot: departmentSnapshot,
      studentId,
      assignedAdminIds,
      participants,
      participantDetails,
      createdBy: studentId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    return {
      success: true,
      conversationId,
      conversation: {
        id: conversationId,
        type: 'department',
        departmentProfileId: departmentProfile.id,
        departmentProfileSnapshot: departmentSnapshot,
        studentId,
        assignedAdminIds,
        participants,
        participantDetails,
        createdBy: studentId,
        lastMessage: null
      }
    };
  } catch (error) {
    console.error('Error creating department conversation:', error);
    return { success: false, message: error.message };
  }
};

export const createConversation = async (participants, participantDetails = [], options = {}) => {
  if (options.type === 'group' || participants.length > 2) {
    return createGroupConversation({
      title: options.title,
      participants,
      participantDetails,
      createdBy: options.createdBy || participants[0]
    });
  }

  return createDirectConversation(participants, participantDetails);
};

export const updateGroupDetails = async (conversationId, userId, updates = {}) => {
  try {
    if (!conversationId || !userId) {
      return { success: false, message: 'Conversation and user are required' };
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnapshot = await getDoc(conversationRef);
    if (!conversationSnapshot.exists()) {
      return { success: false, message: 'Group not found' };
    }

    const conversation = conversationSnapshot.data();
    if (!isGroupAdmin(conversation, userId)) {
      return { success: false, message: 'Only the group admin can edit this group' };
    }

    const payload = {
      updatedBy: userId,
      updatedAt: serverTimestamp()
    };

    if (Object.prototype.hasOwnProperty.call(updates, 'title')) {
      const title = (updates.title || '').trim();
      if (!title) {
        return { success: false, message: 'Group name is required' };
      }
      payload.title = title.slice(0, 50);
    }

    if (Object.prototype.hasOwnProperty.call(updates, 'description')) {
      payload.description = (updates.description || '').trim().slice(0, 180);
    }

    await updateDoc(conversationRef, payload);
    return { success: true };
  } catch (error) {
    console.error('Error updating group details:', error);
    return { success: false, message: error.message };
  }
};

export const updateDepartmentProfile = async (departmentProfileId, userId, updates = {}) => {
  try {
    if (!departmentProfileId || !userId) {
      return { success: false, message: 'Department profile and user are required' };
    }

    const departmentRef = doc(db, 'departmentProfiles', departmentProfileId);
    const departmentSnapshot = await getDoc(departmentRef);
    if (!departmentSnapshot.exists()) {
      return { success: false, message: 'Department profile not found' };
    }

    const payload = {
      updatedBy: userId,
      updatedAt: serverTimestamp()
    };
    const snapshotUpdates = {};

    if (Object.prototype.hasOwnProperty.call(updates, 'name')) {
      const name = (updates.name || '').trim();
      if (!name) {
        return { success: false, message: 'Department name is required' };
      }
      payload.name = name.slice(0, 60);
      payload.normalizedName = normalizeLookupText(name);
      snapshotUpdates.name = payload.name;
    }

    if (Object.prototype.hasOwnProperty.call(updates, 'description')) {
      payload.description = (updates.description || '').trim().slice(0, 180);
      snapshotUpdates.description = payload.description;
    }

    await updateDoc(departmentRef, payload);
    if (Object.keys(snapshotUpdates).length) {
      await syncDepartmentProfileToConversations(departmentProfileId, snapshotUpdates, userId);
    }

    return {
      success: true,
      departmentProfile: {
        id: departmentProfileId,
        ...departmentSnapshot.data(),
        ...payload,
        updatedAt: undefined
      }
    };
  } catch (error) {
    console.error('Error updating department profile:', error);
    return { success: false, message: error.message };
  }
};

export const removeGroupMember = async (conversationId, adminUserId, memberUserId) => {
  try {
    if (!conversationId || !adminUserId || !memberUserId) {
      return { success: false, message: 'Missing group member details' };
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnapshot = await getDoc(conversationRef);
    if (!conversationSnapshot.exists()) {
      return { success: false, message: 'Group not found' };
    }

    const conversation = conversationSnapshot.data();
    if (!isGroupAdmin(conversation, adminUserId)) {
      return { success: false, message: 'Only the group admin can remove members' };
    }

    if (memberUserId === conversation.createdBy) {
      return { success: false, message: 'The group admin cannot be removed' };
    }

    if (!(conversation.participants || []).includes(memberUserId)) {
      return { success: false, message: 'This student is not in the group' };
    }

    const participants = (conversation.participants || []).filter(id => id !== memberUserId);
    const participantDetails = (conversation.participantDetails || []).filter(
      participant => participant.id !== memberUserId
    );

    await updateDoc(conversationRef, {
      participants,
      participantDetails,
      updatedBy: adminUserId,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing group member:', error);
    return { success: false, message: error.message };
  }
};

export const uploadGroupProfilePicture = async (conversationId, userId, file) => {
  try {
    if (!conversationId || !userId || !file) {
      return { success: false, message: 'Missing image upload details' };
    }

    if (!file.type?.match('image.*')) {
      return { success: false, message: 'Please select an image file' };
    }

    if (file.size > 5 * 1024 * 1024) {
      return { success: false, message: 'Image size should be less than 5MB' };
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnapshot = await getDoc(conversationRef);
    if (!conversationSnapshot.exists()) {
      return { success: false, message: 'Group not found' };
    }

    const conversation = conversationSnapshot.data();
    if (!isGroupAdmin(conversation, userId)) {
      return { success: false, message: 'Only the group admin can update the group picture' };
    }

    const resizedFile = await compressImage(file);
    const path = `${GROUP_IMAGE_PATH}/${conversationId}/profile.jpg`;
    const imageRef = storageRef(storage, path);
    await uploadBytes(imageRef, resizedFile, { contentType: 'image/jpeg' });
    const downloadURL = await getDownloadURL(imageRef);

    await updateDoc(conversationRef, {
      groupProfilePicture: downloadURL,
      groupProfilePicturePath: path,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    });

    return { success: true, url: downloadURL, path };
  } catch (error) {
    console.error('Error uploading group picture:', error);
    return { success: false, message: error.message };
  }
};

export const deleteGroupProfilePicture = async (conversationId, userId) => {
  try {
    if (!conversationId || !userId) {
      return { success: false, message: 'Missing group image details' };
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnapshot = await getDoc(conversationRef);
    if (!conversationSnapshot.exists()) {
      return { success: false, message: 'Group not found' };
    }

    const conversation = conversationSnapshot.data();
    if (!isGroupAdmin(conversation, userId)) {
      return { success: false, message: 'Only the group admin can delete the group picture' };
    }

    const path = conversation.groupProfilePicturePath;
    if (path) {
      try {
        await deleteObject(storageRef(storage, path));
      } catch (storageError) {
        console.warn('Could not delete group picture from storage:', storageError);
      }
    }

    await updateDoc(conversationRef, {
      groupProfilePicture: null,
      groupProfilePicturePath: null,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting group picture:', error);
    return { success: false, message: error.message };
  }
};

export const uploadDepartmentProfilePicture = async (departmentProfileId, userId, file) => {
  try {
    if (!departmentProfileId || !userId || !file) {
      return { success: false, message: 'Missing department image details' };
    }

    if (!file.type?.match('image.*')) {
      return { success: false, message: 'Please select an image file' };
    }

    if (file.size > 5 * 1024 * 1024) {
      return { success: false, message: 'Image size should be less than 5MB' };
    }

    const resizedFile = await compressImage(file, 512, 512, 0.82);
    const path = `${DEPARTMENT_IMAGE_PATH}/${departmentProfileId}/profile.jpg`;
    const imageRef = storageRef(storage, path);
    await uploadBytes(imageRef, resizedFile, { contentType: 'image/jpeg' });
    const downloadURL = await getDownloadURL(imageRef);

    await updateDoc(doc(db, 'departmentProfiles', departmentProfileId), {
      profilePicture: downloadURL,
      profilePicturePath: path,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    });
    await syncDepartmentProfileToConversations(departmentProfileId, {
      profilePicture: downloadURL,
      profilePicturePath: path
    }, userId);

    return { success: true, url: downloadURL, path };
  } catch (error) {
    console.error('Error uploading department picture:', error);
    return { success: false, message: error.message };
  }
};

export const deleteDepartmentProfilePicture = async (departmentProfileId, userId) => {
  try {
    if (!departmentProfileId || !userId) {
      return { success: false, message: 'Missing department image details' };
    }

    const departmentRef = doc(db, 'departmentProfiles', departmentProfileId);
    const departmentSnapshot = await getDoc(departmentRef);
    if (!departmentSnapshot.exists()) {
      return { success: false, message: 'Department profile not found' };
    }

    const path = departmentSnapshot.data().profilePicturePath;
    if (path) {
      try {
        await deleteObject(storageRef(storage, path));
      } catch (storageError) {
        console.warn('Could not delete department picture from storage:', storageError);
      }
    }

    await updateDoc(departmentRef, {
      profilePicture: null,
      profilePicturePath: null,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    });
    await syncDepartmentProfileToConversations(departmentProfileId, {
      profilePicture: '',
      profilePicturePath: ''
    }, userId);

    return { success: true };
  } catch (error) {
    console.error('Error deleting department picture:', error);
    return { success: false, message: error.message };
  }
};

export const deleteConversation = async (conversationId, userId) => {
  try {
    if (!conversationId || !userId) {
      return { success: false, message: 'Conversation and user are required' };
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnapshot = await getDoc(conversationRef);
    if (!conversationSnapshot.exists()) {
      return { success: true };
    }

    const conversation = conversationSnapshot.data();
    if (!canDeleteConversation(conversation, userId)) {
      return { success: false, message: 'You do not have permission to delete this conversation' };
    }

    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    let deletedCount = 0;
    let keepDeleting = true;

    while (keepDeleting) {
      const messagesSnapshot = await getDocs(query(messagesRef, limit(MESSAGE_DELETE_LIMIT)));
      if (messagesSnapshot.empty) {
        keepDeleting = false;
        break;
      }

      const batch = writeBatch(db);
      messagesSnapshot.docs.forEach((messageDoc) => {
        batch.delete(messageDoc.ref);
      });
      await batch.commit();
      deletedCount += messagesSnapshot.size;

      keepDeleting = messagesSnapshot.size === MESSAGE_DELETE_LIMIT;
    }

    if (getConversationType(conversation) === 'group' && conversation.groupProfilePicturePath) {
      try {
        await deleteObject(storageRef(storage, conversation.groupProfilePicturePath));
      } catch (storageError) {
        console.warn('Could not delete group picture from storage:', storageError);
      }
    }

    await deleteDoc(conversationRef);
    return { success: true, deletedCount };
  } catch (error) {
    console.error('Error deleting conversation:', error);
    return { success: false, message: error.message };
  }
};

const deleteConversationMessages = async (conversationId) => {
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  let deletedCount = 0;
  let keepDeleting = true;

  while (keepDeleting) {
    const messagesSnapshot = await getDocs(query(messagesRef, limit(MESSAGE_DELETE_LIMIT)));
    if (messagesSnapshot.empty) {
      keepDeleting = false;
      break;
    }

    const batch = writeBatch(db);
    messagesSnapshot.docs.forEach((messageDoc) => {
      batch.delete(messageDoc.ref);
    });
    await batch.commit();
    deletedCount += messagesSnapshot.size;

    keepDeleting = messagesSnapshot.size === MESSAGE_DELETE_LIMIT;
  }

  return deletedCount;
};

export const deleteDepartmentProfile = async (departmentProfileId, userId) => {
  try {
    if (!departmentProfileId || !userId) {
      return { success: false, message: 'Department profile and user are required' };
    }

    const departmentRef = doc(db, 'departmentProfiles', departmentProfileId);
    const departmentSnapshot = await getDoc(departmentRef);
    const profilePicturePath = departmentSnapshot.exists()
      ? departmentSnapshot.data().profilePicturePath
      : '';
    let deletedMessages = 0;
    let deletedConversations = 0;
    let keepDeleting = true;

    while (keepDeleting) {
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('departmentProfileId', '==', departmentProfileId),
        limit(MESSAGE_DELETE_LIMIT)
      );
      const conversationsSnapshot = await getDocs(conversationsQuery);

      if (conversationsSnapshot.empty) {
        keepDeleting = false;
        break;
      }

      for (const conversationDoc of conversationsSnapshot.docs) {
        deletedMessages += await deleteConversationMessages(conversationDoc.id);
        await deleteDoc(conversationDoc.ref);
        deletedConversations += 1;
      }

      keepDeleting = conversationsSnapshot.size === MESSAGE_DELETE_LIMIT;
    }

    if (profilePicturePath) {
      try {
        await deleteObject(storageRef(storage, profilePicturePath));
      } catch (storageError) {
        console.warn('Could not delete department picture from storage:', storageError);
      }
    }

    await deleteDoc(departmentRef);

    return {
      success: true,
      deletedConversations,
      deletedMessages
    };
  } catch (error) {
    console.error('Error deleting department profile:', error);
    return { success: false, message: error.message };
  }
};

export const getConversations = async (userId) => {
  try {
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('updatedAt', 'desc'),
      limit(CONVERSATION_LIMIT)
    );

    const querySnapshot = await getDocs(conversationsQuery);
    const conversations = querySnapshot.docs.map(conversationDoc => ({
      id: conversationDoc.id,
      ...conversationDoc.data()
    }));

    return { success: true, conversations };
  } catch (error) {
    console.error('Error getting conversations:', error);
    return { success: false, message: error.message };
  }
};

export const getMessages = async (conversationId) => {
  try {
    const messagesQuery = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(MESSAGE_LIMIT)
    );

    const querySnapshot = await getDocs(messagesQuery);
    const messages = querySnapshot.docs
      .map(messageDoc => ({
        id: messageDoc.id,
        ...messageDoc.data()
      }))
      .reverse();

    return { success: true, messages };
  } catch (error) {
    console.error('Error getting messages:', error);
    return { success: false, message: error.message };
  }
};

export const sendMessage = async (messageData) => {
  try {
    const {
      conversationId,
      senderId,
      senderName,
      text,
      timestamp,
      senderType,
      sentBy,
      departmentProfileId
    } = messageData;
    const cleanText = (text || '').trim();

    if (!conversationId || !senderId || !cleanText) {
      return { success: false, message: 'Missing required message data' };
    }

    const messageTimestamp = timestamp || serverTimestamp();
    const messagePayload = {
      senderId,
      senderName: senderName || 'Student',
      text: cleanText,
      timestamp: messageTimestamp
    };

    if (senderType) messagePayload.senderType = senderType;
    if (sentBy) messagePayload.sentBy = sentBy;
    if (departmentProfileId) messagePayload.departmentProfileId = departmentProfileId;

    await addDoc(collection(db, 'conversations', conversationId, 'messages'), messagePayload);

    await updateDoc(doc(db, 'conversations', conversationId), {
      lastMessage: {
        text: cleanText,
        senderId,
        senderName: senderName || 'Student',
        timestamp: messageTimestamp,
        ...(senderType ? { senderType } : {}),
        ...(sentBy ? { sentBy } : {}),
        ...(departmentProfileId ? { departmentProfileId } : {})
      },
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error sending message:', error);
    return { success: false, message: error.message };
  }
};

export const subscribeToMessages = (conversationId, callback) => {
  if (!conversationId || !callback) return null;

  const messagesQuery = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('timestamp', 'desc'),
    limit(MESSAGE_LIMIT)
  );

  return onSnapshot(messagesQuery, (querySnapshot) => {
    const messages = querySnapshot.docs
      .map(messageDoc => ({
        id: messageDoc.id,
        ...messageDoc.data()
      }))
      .reverse();

    callback(messages);
  }, error => {
    console.error('Error in messages listener:', error);
  });
};

export const subscribeToConversations = (userId, callback) => {
  if (!userId || !callback) return null;

  const conversationsQuery = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc'),
    limit(CONVERSATION_LIMIT)
  );

  return onSnapshot(conversationsQuery, (querySnapshot) => {
    const conversations = querySnapshot.docs.map(conversationDoc => ({
      id: conversationDoc.id,
      ...conversationDoc.data()
    }));

    callback(conversations);
  }, error => {
    console.error('Error in conversations listener:', error);
  });
};

export function useChat(currentUserId) {
  const conversations = ref([]);
  const currentConversation = ref(null);
  const messages = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const unsubscribeMessages = ref(null);
  const unsubscribeConversations = ref(null);

  const fetchChats = () => {
    loading.value = true;
    error.value = null;

    if (unsubscribeConversations.value) {
      unsubscribeConversations.value();
    }

    unsubscribeConversations.value = subscribeToConversations(currentUserId, (conversationList) => {
      conversations.value = conversationList;
      loading.value = false;
    });

    return unsubscribeConversations.value;
  };

  const startChat = async (otherUserId, participantDetails = []) => {
    loading.value = true;
    error.value = null;

    const result = await createDirectConversation([currentUserId, otherUserId], participantDetails);
    if (!result.success) {
      error.value = result.message || 'Failed to start chat';
      loading.value = false;
      return null;
    }

    currentConversation.value = result.conversation || {
      id: result.conversationId,
      participants: [currentUserId, otherUserId],
      type: 'direct'
    };
    loading.value = false;
    return result.conversationId;
  };

  const selectChat = async (conversationId) => {
    if (unsubscribeMessages.value) {
      unsubscribeMessages.value();
      unsubscribeMessages.value = null;
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnapshot = await getDoc(conversationRef);
    if (!conversationSnapshot.exists()) {
      error.value = 'Conversation not found';
      return false;
    }

    currentConversation.value = {
      id: conversationSnapshot.id,
      ...conversationSnapshot.data()
    };
    unsubscribeMessages.value = subscribeToMessages(conversationId, (messageList) => {
      messages.value = messageList;
    });
    return true;
  };

  const sendCurrentMessage = async (messageText, senderName = 'Student') => {
    if (!currentConversation.value?.id) {
      error.value = 'No chat selected';
      return false;
    }

    const result = await sendMessage({
      conversationId: currentConversation.value.id,
      senderId: currentUserId,
      senderName,
      text: messageText
    });

    if (!result.success) {
      error.value = result.message || 'Failed to send message';
    }

    return result.success;
  };

  const cleanUp = () => {
    if (unsubscribeMessages.value) unsubscribeMessages.value();
    if (unsubscribeConversations.value) unsubscribeConversations.value();
    unsubscribeMessages.value = null;
    unsubscribeConversations.value = null;
  };

  return {
    chats: conversations,
    conversations,
    currentChat: currentConversation,
    currentConversation,
    messages,
    loading,
    error,
    fetchChats,
    startChat,
    selectChat,
    sendMessage: sendCurrentMessage,
    markChatAsRead: async () => true,
    hasUnreadMessages: ref(false),
    unreadChatsCount: ref(0),
    cleanUp
  };
}
